import classNames from 'classnames';
import React from 'react';

import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {capitalize} from 'util/NameUtil';

import {ASCENDING_SORT, CreateCustomSortElement, ScrollableTableColumn} from './ScrollableTableConstants';

export interface ScrollableTableHeaderCellProps {
  checkbox?: React.ReactNode;
  className?: string;
  column: ScrollableTableColumn;
  handleSort?: (sortKey?: string | null) => void;
  handleToggleCollapse?: (column: ScrollableTableColumn) => void;
  hasLineBelowHeader?: boolean;
  headerCellNode?: React.ReactNode;
  isCollapsed: boolean;
  isPrimary?: boolean;
  noRightBorder?: boolean;
  createCustomSortElement?: CreateCustomSortElement;
}

class ScrollableTableHeaderCell extends React.Component<ScrollableTableHeaderCellProps> {
  static defaultProps = {hasLineBelowHeader: false, isPrimary: false};

  private handleSort = (): void => {
    this.props.handleSort!(this.props.column.sortKey);
  };

  private handleToggleCollapse = (): void => {
    if (this.props.column.isCollapsible) {
      this.props.handleToggleCollapse!(this.props.column);
    }
  };

  render(): JSX.Element {
    const isCollapsible = this.props.column.isCollapsible;
    const isSorted = this.props.column.sortDirection != null;
    const headerClass = classNames('scrollableTable-headerCell', {
      'scrollableTable-cell--isPrimary': this.props.isPrimary,
      'scrollableTable-cell--noRightBorder': this.props.noRightBorder,
      'scrollableTable-headerCell--border': this.props.hasLineBelowHeader,
    });
    return (
      <div className={headerClass}>
        {this.props.headerCellNode && <div className="u-position-absolute">{this.props.headerCellNode}</div>}
        <div className="u-position-relative">
          {this.props.checkbox}
          <div className={classNames('scrollableTable-headerCell-content', this.props.className)}>
            <Tooltip
              isDisabled={this.props.column.header === ''}
              text={this.props.column.header}
              placement={FloatingPlacement.TOP}
              disableContentSelection={true}
            >
              <span
                className={classNames('scrollableTable-headerCell-text', {
                  'u-ellipsis-text': !this.props.isCollapsed,
                  'is-collapsible': isCollapsible && !this.props.isCollapsed,
                  'is-collapsed': isCollapsible && this.props.isCollapsed,
                  'scrollableTableRow-iconCellText': this.props.checkbox != null,
                })}
                onClick={this.handleToggleCollapse}
              >
                {this.props.column.icon ? (
                  <BenchlingIcon
                    className="scrollableTable-icon scrollableTable-headerCell-icon"
                    icon={this.props.column.icon}
                  />
                ) : null}
                {isCollapsible && (
                  <BenchlingIcon
                    className="scrollableTable-icon scrollableTable-headerCell-collapse"
                    icon={this.props.isCollapsed ? 'chevron' : 'chevron-left'}
                  />
                )}
                {this.props.isCollapsed ? null : this.props.column.header}
              </span>
            </Tooltip>
            {(() => {
              if (this.props.column.isSortable && !this.props.isCollapsed) {
                const sortIconClassNames = classNames(
                  'scrollableTable-icon scrollableTable-headerCell-rightIcon',
                  'scrollableTable-headerCell-sort',
                  {
                    'is-sorted': isSorted,
                  }
                );
                if (this.props.createCustomSortElement) {
                  return this.props.createCustomSortElement({column: this.props.column, sortIconClassNames});
                }
                const icon = (
                  <BenchlingIcon
                    className={sortIconClassNames}
                    icon={this.props.column.sortDirection === ASCENDING_SORT ? 'arrow-up' : 'arrow-down'}
                    onClick={this.handleSort}
                  />
                );
                if (isSorted) {
                  return (
                    <div className="scrollableTable-headerCell-rightIcon">
                      <Tooltip
                        placement={FloatingPlacement.TOP}
                        text={`Sorting by ${this.props.column.header} (${capitalize(
                          this.props.column.sortDirection
                        )})`}
                      >
                        {icon}
                      </Tooltip>
                    </div>
                  );
                }
                return <div className="scrollableTable-headerCell-rightIcon">{icon}</div>;
              } else if (this.props.column.handleCreate) {
                return (
                  <BenchlingIcon
                    className="scrollableTable-headerCell-rightIcon scrollableTable-headerCell-compose"
                    icon="compose"
                    onClick={this.props.column.handleCreate}
                  />
                );
              }
              return null;
            })()}
          </div>
        </div>
      </div>
    );
  }
}

export default ScrollableTableHeaderCell;
